<template>
  <div class="page">
    <!-- HEADER -->
    <site-header is-static />

    <v-main>
      <!-- MAIN -->
      <div
        v-if="!isLoading"
        class="app-content site-width"
      >
        <div class="app-content__spacer">
          <custom-validation-observer
            ref="formValidationObserver"
            v-slot="formObserver"
            tag="div"
            class="form-car-wrapper"
            @valid="onValid"
          >
            <!-- BREADCRUMBS -->
            <steps
              class="breadcrumbs"
              :steps="formConfigData"
              @go-to-step="(val) => onGoToStep({...val,formObserver})"
            />

            <qz-cta
              class="mb-4"
              :title="activeStepTitle"
              :current-step="currentStep"
            >
              <template #firstStepNote>
                <medical-conditions-info class="mb-6" />
              </template>
              <template #firstStepInfo>
                <p>
                  <strong>Let's save some money!</strong>
                  Take care to answer questions accurately. Incomplete information could see your policy cancelled or a claim rejected.
                </p>
              </template>
            </qz-cta>

            <!-- FORM -->
            <form
              id="form"
              novalidate
              sticky-container
              @submit.prevent
              @keydown.enter.prevent
            >
              <!-- FORM STEPS -->
              <div class="step-wrap">
                <component
                  :is="stepComponent"
                  ref="step"
                  :form="form"
                  :form-observer="patchObserver(formObserver)"
                  :step-structure="travelStructure[currentStep - 1]"
                  :is-experiment-travel-minimise-helptext="isExperimentTravelMinimiseHelptext"
                  @loading="isProcessing = $event"
                  @step-data="stepData = $event"
                  @tracking="gtmTracking"
                />
              </div>

              <!-- ERRORS SECTION -->
              <core-step-errors
                :active-step="activeStep"
                :structure="travelStructure"
                :component-key="componentKey"
                :form-observer="formObserver"
                :is-validated="isValidated"
                :step-ref="$refs.step"
              />

              <qz-form-actions
                ref="formActions"
                :current-step="currentStep"
                :form-observer="formObserver"
                :component-key="componentKey"
                :show-actions="showActions"
                :is-last-step="isLastStep"
                @prev-step="prevStep(formObserver)"
                @next-step="nextStep(formObserver)"
                @captcha-submit="validateBeforeRecaptcha(formObserver)"
              />
            </form>
          </custom-validation-observer>
        </div>
      </div>

      <quote-limit-exceed-popup v-model="quoteLimitExceeded" />

      <!-- FOOTER -->
      <template v-if="!isLoading">
        <site-footer
          v-if="config.partnerFooter"
          role="footer"
        >
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>

        <branding-logo
          v-if="config.brandingLogo"
          :branding-type="config.brandingType"
        />

        <bespoke-footer-information
          v-if="showBespokeFooterInformation"
          :content="config.bespokeFooterInformation"
        />
      </template>
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="isLoading || isProcessing" />
  </div>
</template>

<script>
import useQuotezoneForm from '@/composables/useQuotezoneForm';
import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import { getQuotePayload } from '@/payloadTemplates/travel';
import {
  inject,
  computed,
} from '@vue/composition-api';

import Steps from '@/components/Steps/Steps';

import { travelStructure } from '@/helpers/products/travel';
import Step1 from '@/views/travel/steps/Step1';
import Step2 from '@/views/travel/steps/Step2';
import MedicalConditionsInfo from '@/components/shared/MedicalConditionsInfo';
import QzCta from '@/components/QzCta/QzCta';
import QzFormActions from '@/components/QzFormActions.vue';
import CoreStepErrors from '@/components/StepErrors/CoreStepErrors.vue';

export default {
  name: 'page-travel-insurance',
  metaInfo () {
    const defaultTitle = `${this.config.partnerText} | Travel Insurance`;
    return {
      title: this.config.partnerPageFormTitle || defaultTitle,
    };
  },
  components: {
    CoreStepErrors,
    QzFormActions,
    Steps,
    Step1,
    Step2,
    MedicalConditionsInfo,
    QzCta,
  },
  async created () {
    await this.init();
  },
  setup (props, context) {
    const config = inject('config');
    const helpers = inject('helpers');
    const {
      isTravelMinimiseHelptext,
    } = inject('abTastyControllerUI');

    // COMPUTED
    const isExperimentTravelMinimiseHelptext = computed(() => isTravelMinimiseHelptext.value && helpers.isQuotezonePartner(config.partner));

    return {
      ...useQuotezoneCommon(null, context),
      ...useQuotezoneForm({
        payload: getQuotePayload(inject('config')),
        structure: travelStructure,
      }, context),
      isExperimentTravelMinimiseHelptext,
    };
  },
};
</script>

<style scoped lang="scss">
.step-wrap {
  border-radius: 6px;
  background: $clr-white;
  border: 1px solid $clr-heather;
  padding: 5px 0;
}

// TODO: refactor - should be moved to common styles
.travel-form {
  display: flex;
  align-items: flex-start;
  position: relative;

  &__content {
    flex: 1;
  }

  &__sidebar {
    flex: 0 0 190px;
    width: 190px;
  }
}
</style>
<style lang="scss" scoped src="@/assets/scss/shared/breadcrumbs.scss"/>
<style lang="scss" scoped src="@/assets/scss/page-width.scss"/>
